import {
    Box,
    Badge,
    Spinner,
    Flex,
    Table,
    Text,
    Menu,
} from "@chakra-ui/react";

import {
    useEffect,
    useState,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    MoreVertical,
    Eye,
    Trash2,
    Plus,
} from "lucide-react";

import Sidebar from "../../../components/Sidebar";
import TopBar from "../../../components/Topbar";

import {
    fetchMySupportTickets,
} from "../../../state/actions/supportActions";

import DataTable from "../../../components/DataTable";

import TicketDetailsDrawer from "./ViewTicketDrawer";
import SupportTicketModal from "./SupportTicketModal";
import EmptyState from "../../../components/EmptyState";

const columns = [
    {
        key: "ticket_id",
        label: "Ticket ID",
    },
    {
        key: "subject",
        label: "Subject",
    },
    {
        key: "category",
        label: "Category",
    },
    {
        key: "status",
        label: "Status",
    },
    {
        key: "attachments",
        label: "Attachments",
    },
    {
        key: "updated_at",
        label: "Last Updated",
    },
    {
        key: "actions",
        label: "Actions",
        align: "center",
    },
];

const statusColor = {
    OPEN: "blue",
    IN_PROGRESS: "orange",
    RESOLVED: "green",
    CLOSED: "gray",
};

const SupportTickets = () => {
    const dispatch =
        useDispatch();

    const [
        isOpen,
        setIsOpen,
    ] = useState(false);

    const [
        category,
        setCategory,
    ] = useState(
        "RECRUITMENT_AND_REFERRALS"
    );

    const [
        selectedTicket,
        setSelectedTicket,
    ] = useState(null);

    const [
        drawerOpen,
        setDrawerOpen,
    ] = useState(false);

    const {
        tickets = [],
        loading,
    } = useSelector(
        state =>
            state.support
    );

    useEffect(() => {
        dispatch(
            fetchMySupportTickets(
                1,
                50
            )
        );
    }, [dispatch]);

    const formatDate = (
        date
    ) => {
        if (!date) {
            return "-";
        }

        return new Date(
            date
        ).toLocaleDateString(
            "en-GB",
            {
                day: "numeric",
                month: "short",
                year: "numeric",
            }
        );
    };

    if (loading) {
        return (
            <Flex
                h="100vh"
                bg="#F8FAFC"
                overflow="hidden"
            >
                <Box
                    w={{
                        base: 0,
                        md: "280px",
                    }}
                    flexShrink={0}
                >
                    <Sidebar />
                </Box>

                <Flex
                    flex="1"
                    direction="column"
                    overflow="hidden"
                >
                    <TopBar />

                   <Flex
                    flex="1"
                    direction="column"
                    justify="center"
                    align="center"
                    gap={4}
                >
                    <Spinner
                        size="lg"
                        color="#2453FF"
                    />

                    <Text
                        color="gray.500"
                        fontSize="sm"
                        fontWeight="500"
                    >
                        Loading support tickets...
                    </Text>
                </Flex>
                </Flex>
            </Flex>
        );
    }

    return (
        <Flex
            h="100vh"
            bg="#F8FAFC"
            overflow="hidden"
        >
            <Box
                w={{
                    base: 0,
                    md: "280px",
                }}
                flexShrink={0}
            >
                <Sidebar />
            </Box>

            <Flex
                flex="1"
                direction="column"
                overflow="hidden"
                minW={0}
            >
                <TopBar />

                <Box
                    flex="1"
                    overflowY="auto"
                    p={{
                        base: 4,
                        md: 8,
                    }}
                >
                    <DataTable
                        title="Support Tickets"
                        total={
                            tickets.length
                        }
                        data={
                            tickets
                        }
                        columns={
                            columns
                        }
                        pageSize={10}
                        loading={
                            loading
                        }
                        filters={[
                            "All",
                            "OPEN",
                            "IN_PROGRESS",
                            "RESOLVED",
                            "CLOSED",
                        ]}
                        defaultFilter="All"
                        emptyComponent={
                        <EmptyState
                            imageSize="180px"
                            title="No Support Tickets"
                            message="You haven't submitted any support tickets yet. Your support requests will appear here once they're created."
                            py={12}
                        />
                    }
                        rightAction={
                            <Flex
                                w={{
                                    base: "100%",
                                    md: "auto",
                                }}
                            >
                                <Box
                                    w={{
                                        base: "100%",
                                        md: "auto",
                                    }}
                                    bg="#2453FF"
                                    color="white"
                                    px={4}
                                    py={2}
                                    borderRadius="12px"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    gap={2}
                                    cursor="pointer"
                                    fontWeight="600"
                                    _hover={{
                                        bg: "#1D4ED8",
                                    }}
                                    onClick={() =>
                                        setIsOpen(
                                            true
                                        )
                                    }
                                >
                                    <Plus
                                        size={16}
                                    />

                                    <Text>
                                        Send Request
                                    </Text>
                                </Box>
                            </Flex>
                        }
                        renderRow={ticket => (
                            <Table.Row
                                key={
                                    ticket.ticket_id
                                }
                            >
                                <Table.Cell>
                                    #
                                    {ticket.ticket_id?.slice(
                                        0,
                                        8
                                    )}
                                </Table.Cell>

                                <Table.Cell
                                    maxW={{
                                        base: "140px",
                                        md: "220px",
                                    }}
                                >
                                    <Text
                                        textTransform="capitalize"
                                        whiteSpace="nowrap"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                    >
                                        {ticket.category
                                            ?.replaceAll(
                                                "_",
                                                " "
                                            )
                                            .toLowerCase()}
                                    </Text>
                                </Table.Cell>

                                <Table.Cell>
                                    <Text
                                        fontWeight="600"
                                        whiteSpace="nowrap"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                    >
                                        {
                                            ticket.subject
                                        }
                                    </Text>
                                </Table.Cell>

                                <Table.Cell>
                                    <Badge
                                        colorPalette={
                                            statusColor[
                                                ticket.status
                                            ] ||
                                            "gray"
                                        }
                                        borderRadius="full"
                                        px={3}
                                    >
                                        {
                                            ticket.status
                                        }
                                    </Badge>
                                </Table.Cell>

                                <Table.Cell>
                                    {ticket
                                        .documents
                                        ?.length ||
                                        0}
                                </Table.Cell>

                                <Table.Cell>
                                    {formatDate(
                                        ticket.updated_at
                                    )}
                                </Table.Cell>

                                <Table.Cell textAlign="center">
                                    <Menu.Root>
                                        <Menu.Trigger
                                            asChild
                                        >
                                            <Box
                                                cursor="pointer"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                w={{
                                                    base: "40px",
                                                    md: "32px",
                                                }}
                                                h={{
                                                    base: "40px",
                                                    md: "32px",
                                                }}
                                                borderRadius="8px"
                                                _hover={{
                                                    bg: "gray.100",
                                                }}
                                            >
                                                <MoreVertical
                                                    size={
                                                        16
                                                    }
                                                />
                                            </Box>
                                        </Menu.Trigger>

                                        <Menu.Positioner>
                                            <Menu.Content>
                                                <Menu.Item
                                                    value="view"
                                                    onClick={() => {
                                                        setSelectedTicket(
                                                            ticket
                                                        );

                                                        setDrawerOpen(
                                                            true
                                                        );
                                                    }}
                                                >
                                                    <Eye
                                                        size={
                                                            16
                                                        }
                                                    />

                                                    View Ticket
                                                </Menu.Item>

                                                <Menu.Item
                                                    value="delete"
                                                >
                                                    <Trash2
                                                        size={
                                                            16
                                                        }
                                                    />

                                                    Delete Ticket
                                                </Menu.Item>
                                            </Menu.Content>
                                        </Menu.Positioner>
                                    </Menu.Root>
                                </Table.Cell>
                            </Table.Row>
                        )}
                    />
                </Box>
            </Flex>

            <TicketDetailsDrawer
                ticket={
                    selectedTicket
                }
                open={
                    drawerOpen
                }
                onClose={() =>
                    setDrawerOpen(
                        false
                    )
                }
            />

            <SupportTicketModal
                isOpen={isOpen}
                onClose={() =>
                    setIsOpen(
                        false
                    )
                }
                category={category}
                setCategory={
                    setCategory
                }
            />
        </Flex>
    );
};

export default SupportTickets;